import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useGoogleDriveConnection } from "@/hooks/cloud/useGoogleDriveConnection";
import { useOneDriveConnection } from "@/hooks/cloud/useOneDriveConnection";
import { cn } from "@/lib/utils";

interface CloudProviderCardProps {
  provider: "google_drive" | "onedrive";
  onBrowseClick?: () => void;
  browseLabel?: string;
}

const CloudProviderCard = ({ provider, onBrowseClick, browseLabel = "Browse Files" }: CloudProviderCardProps) => {
  const googleDrive = useGoogleDriveConnection();
  const oneDrive = useOneDriveConnection();

  const { connected, email, displayName, connect, disconnect, isDisconnecting, isLoading } =
    provider === "google_drive" ? googleDrive : oneDrive;
  const [isConnecting, setIsConnecting] = useState(false);

  const isGoogleDrive = provider === "google_drive";
  const isOneDrive = provider === "onedrive";

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      connect();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to connect Google Drive";
      toast.error(message);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to disconnect";
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 rounded-lg border-2 border-gray-200 bg-white">
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "p-6 rounded-lg border-2 transition-all",
        connected
          ? "border-genie-primary bg-gradient-to-br from-genie-primary/5 to-genie-secondary/5"
          : "border-gray-200 bg-white",
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {isGoogleDrive && (
            <img
              src="/icons/google_drive.png"
              alt="Google Drive"
              className="h-10 w-10"
            />
          )}
          {isOneDrive && (
            <img
              src="/icons/onedrive.png"
              alt="OneDrive"
              className="h-10 w-10"
            />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{isGoogleDrive ? "Google Drive" : "OneDrive"}</h3>
            {connected && email && <p className="text-sm text-gray-600">Connected as <CopyableEmail email={email} /></p>}
            {!connected && <p className="text-sm text-gray-500">Not connected</p>}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        {!connected ? (
          <Button
            onClick={handleConnect}
            disabled={isConnecting || isDisconnecting}
            className="flex-1 bg-gradient-to-r from-genie-primary to-genie-secondary text-white hover:shadow-lg"
          >
            {isConnecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Connecting...
              </>
            ) : (
              `Connect ${isGoogleDrive ? "Google Drive" : "OneDrive"}`
            )}
          </Button>
        ) : (
          <>
            <Button
              onClick={onBrowseClick}
              disabled={isDisconnecting}
              className="flex-1 bg-genie-primary text-white hover:shadow-lg"
            >
              {browseLabel}
            </Button>
            <Button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              variant="outline"
              size="icon"
              className="text-red-600 hover:bg-red-50"
            >
              {isDisconnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CloudProviderCard;
